import type { UploadParams, CancelHandle } from '../type';
import { httpRequest } from '../utils/httpRequest';
import {
  assertFile,
  createCancelHandle,
  createMultipartUploadId,
  createProgressSnapshot,
  getUploadConcurrency,
  getUploadPartSize,
  resolveUploadFileName,
  runWithConcurrency,
  shouldUseMultipartUpload,
} from '../utils/utils';

interface MultipartChunk {
  index: number;
  blob: Blob;
  size: number;
  loaded: number;
  uploadId: string;
  abortHandle: ReturnType<typeof createCancelHandle>;
}

export async function upload(params: UploadParams): Promise<unknown> {
  const file = assertFile(params.file);
  const { uploadUrl = '', mergeUrl = '' } = params;

  if (!uploadUrl) {
    throw new Error('ObjectStorage: uploadUrl is required for normal upload');
  }

  const fileName = resolveUploadFileName(params);
  const isMultipart = shouldUseMultipartUpload(file, params.maxSize);

  if (!isMultipart) {
    return uploadWhole(file, fileName, uploadUrl, params);
  }

  return uploadMultipart(file, fileName, uploadUrl, mergeUrl, params);
}

async function uploadWhole(
  file: File,
  fileName: string,
  uploadUrl: string,
  params: UploadParams
): Promise<unknown> {
  const abortHandle = createCancelHandle(params.onCancel);
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', fileName);

  return httpRequest({
    url: uploadUrl,
    method: 'post',
    data: formData,
    onProgress: params.onUploadProgress,
    xhrInstance: abortHandle,
  });
}

async function uploadMultipart(
  file: File,
  fileName: string,
  uploadUrl: string,
  mergeUrl: string,
  params: UploadParams
): Promise<unknown> {
  if (!mergeUrl) {
    throw new Error('ObjectStorage: mergeUrl is required for multipart upload');
  }

  const partSize = getUploadPartSize(params.partSize);
  const concurrency = getUploadConcurrency(params.queueLength);
  const totalChunks = Math.ceil(file.size / partSize);
  const uploadId = createMultipartUploadId();
  const mergeAbortHandle = createCancelHandle();

  let uploadedBytes = 0;
  const chunks: MultipartChunk[] = [];

  const cancelAll: CancelHandle = {
    instance: null,
    cancel() {
      chunks.forEach((c) => c.abortHandle.cancel());
      mergeAbortHandle.cancel();
    },
  };
  params.onCancel?.(cancelAll);

  for (let i = 1; i <= totalChunks; i++) {
    const start = (i - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    chunks.push({
      index: i,
      blob,
      size: blob.size,
      loaded: 0,
      uploadId,
      abortHandle: createCancelHandle(),
    });
  }

  const notifyProgress = () => {
    params.onUploadProgress?.(createProgressSnapshot(uploadedBytes, file.size));
  };

  try {
    await runWithConcurrency(chunks, concurrency, async (chunk) => {
      const data = new FormData();
      data.append('file', chunk.blob);
      data.append('uploadId', chunk.uploadId);
      data.append('chunk', chunk.index.toString());
      data.append('chunks', totalChunks.toString());
      data.append('fileName', fileName);

      await httpRequest({
        url: uploadUrl,
        method: 'post',
        data,
        xhrInstance: chunk.abortHandle,
        onProgress: (progress) => {
          const nextLoaded = Math.min(progress.loaded, chunk.size);
          if (nextLoaded === chunk.loaded) return;

          uploadedBytes += nextLoaded - chunk.loaded;
          chunk.loaded = nextLoaded;
          notifyProgress();
        },
      });

      if (chunk.loaded !== chunk.size) {
        uploadedBytes += chunk.size - chunk.loaded;
        chunk.loaded = chunk.size;
        notifyProgress();
      }
    });
  } catch (error) {
    cancelAll.cancel();
    throw error;
  }

  const query = new URLSearchParams({ uploadId, fileName });
  return httpRequest({
    url: `${mergeUrl}?${query.toString()}`,
    method: 'get',
    xhrInstance: mergeAbortHandle,
  });
}
